import { KakaoBrandMessageDoc } from '@gooduncles/gu-app-schema';
import { SelectionChangedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useCallback, useMemo, useState } from 'react';

import { formatDateForAgCell } from 'src/utils/ag-grid-util';

import KakaotalkContentPopover from '../../AlimtalkMessage/AlimtalkContentPopover/AlimtalkContentPopover';

interface KakaoBrandMessageResultTableProps {
  rowData: KakaoBrandMessageDoc[];
  onSelectionChanged?: (selectedIds: string[]) => void;
}

const defaultColDef: ColDef<KakaoBrandMessageDoc> = {
  flex: 1,
  sortable: true,
  resizable: true,
  filter: true,
};

const KakaoBrandMessageResultTable: FC<KakaoBrandMessageResultTableProps> = ({ rowData, onSelectionChanged }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<KakaoBrandMessageDoc> | null>(null);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<KakaoBrandMessageDoc>) => {
      const selectedRows = event.api.getSelectedRows();
      const selectedIds = selectedRows.map((row: KakaoBrandMessageDoc) => row._id);
      onSelectionChanged?.(selectedIds);
    },
    [onSelectionChanged]
  );

  const columnDefs: ColDef<KakaoBrandMessageDoc>[] = useMemo(() => {
    return [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        maxWidth: 50,
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        field: '_id',
        hide: true,
      },
      {
        headerName: 'requestId',
        field: 'requestId',
        hide: true,
      },
      {
        headerName: '요청일시',
        field: '_timeCreate',
        valueFormatter: formatDateForAgCell('L월 d일(EEEEEE) HH:mm:ss'),
      },
      {
        headerName: '수신번호',
        field: 'recipientNo',
      },
      {
        headerName: '수신자',
        field: 'recipientGroupingKey',
      },
      {
        headerName: '발신구분',
        field: 'senderGroupingKey',
        valueFormatter: ({ value }) => {
          if (value === 'daily-price-list') return '일일 가격 알림';
          if (value === 'custom') return '커스텀 메시지';
          return value ?? '-';
        },
      },
      {
        headerName: '메시지타입',
        field: 'chatBubbleType',
      },
      {
        headerName: '요청결과',
        valueGetter: ({ data }) => {
          const { postResponse } = data as KakaoBrandMessageDoc;
          if (postResponse) {
            return postResponse.resultCode === 0 ? '🟢 성공' : `❌ ${postResponse.resultMessage}`;
          }
          return data?.error ? `❌ ${data.error}` : '-';
        },
      },
      {
        headerName: '발송결과',
        valueGetter: ({ data }) => {
          const { messageResult, requestDate } = data as KakaoBrandMessageDoc;
          if (messageResult?.resultCode) {
            return messageResult.resultCode === '1000'
              ? `🟢 ${messageResult.resultCodeName}`
              : `❌ ${messageResult.resultCodeName}`;
          }
          if (messageResult?.messageStatus) {
            return messageResult.messageStatus === 'COMPLETED' ? '🟢 발송 완료' : `⏳ ${messageResult.messageStatus}`;
          }
          if (requestDate) {
            return `⏰ 예약발송: ${requestDate}`;
          }
          return '⚠️ 결과 없음';
        },
      },
      {
        headerName: '수신일시',
        valueGetter: ({ data }) => {
          const { messageResult } = data as KakaoBrandMessageDoc;
          return messageResult?.receiveDate ?? '-';
        },
      },
      {
        headerName: '발송내용',
        field: 'content',
        cellRenderer: KakaotalkContentPopover,
        cellRendererParams: ({ data }: { data: KakaoBrandMessageDoc }) => {
          if (data?.buttons) {
            return {
              buttons: data.buttons,
            };
          }
        },
      },
    ];
  }, []);

  const autoSizeAll = useCallback(() => {
    gridRef?.columnApi?.autoSizeAllColumns(true);
  }, [gridRef]);

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        ref={setGridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onGridReady={autoSizeAll}
        getRowId={(params) => params.data._id}
        suppressScrollOnNewData={true}
        tooltipShowDelay={0}
        rowSelection='multiple'
        onSelectionChanged={handleSelectionChanged}
        suppressRowClickSelection={true}
      />
    </div>
  );
};

export default KakaoBrandMessageResultTable;
